// src/components/AutoAssignRules.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
	Button,
	Input,
	Label,
	Collapse,
	Badge
} from 'reactstrap';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
	DragDropContext,
	Droppable,
	Draggable
} from 'react-beautiful-dnd';
import AutoAssignRuleWizard from './AutoAssignRuleWizard';
import { AutoAssignConfig, Rule, AssignmentField } from './AutoAssignTypes';
import { STRATEGIES } from './AutoAssignTypes';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { studentsMetadataGetRequest } from 'store/student/action';

interface Props extends WithTranslation {
	config: AutoAssignConfig;
	onChange: (cfg: AutoAssignConfig) => void;
}

const AutoAssignRules: React.FC<Props> = ({ config, onChange, t }) => {
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
	const metaDataFields = useSelector((state: ApplicationState) => state.student.metaDataFields);
	const [enabled, setEnabled] = useState(config?.enabled);
	const [mode, setMode] = useState(config?.mode ? config?.mode : 'FIRST_MATCH');
	const [rules, setRules] = useState<Rule[]>(config?.rules ? config?.rules : []);
	const [openIds, setOpenIds] = useState<string[]>([]);
	const [wizOpen, setWizOpen] = useState(false);
	const [editRule, setEditRule] = useState<Rule | undefined>(undefined);

	useEffect(() => {
		dispatch(studentsMetadataGetRequest("students", { pageSize: 500 }));
	}, []);

	useEffect(() => {
		onChange({ enabled, mode, rules });
	}, [enabled, mode, rules]);

	const assignmentFields: AssignmentField[] = useMemo(() => {
		return metaDataFields
			.filter((f: any) => {
				const json = typeof f.valuesJson === 'string' ? JSON.parse(f.valuesJson) : f.valuesJson;
				return json?.model === 'users';
			})
			.map((f: any) => {
				const json = typeof f.valuesJson === 'string' ? JSON.parse(f.valuesJson) : f.valuesJson;
				return {
					key: json.key,
					label: json.label,
					multiple: json.multiple ?? true,
					options: []
				};
			});
	}, [metaDataFields]);

	const handleDragEnd = (result: any) => {
		if (!result.destination) return;
		const updated = Array.from(rules);
		const [moved] = updated.splice(result.source.index, 1);
		updated.splice(result.destination.index, 0, moved);
		setRules(updated.map((r, i) => ({ ...r, priority: i + 1 })));
	};

	const toggle = (id: string) => {
		setOpenIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	return (
		<div>
			{/* ─── Header ───────────────────────────────────────── */}
			<div className="d-flex justify-content-between align-items-center mb-4">
				<div className="form-check form-switch">
					<Input
						type="switch"
						id="autoAssignSwitch"
						checked={enabled}
						onChange={() => setEnabled(e => !e)}
					/>
					<div className="ms-2">
						<Label for="autoAssignSwitch" className="fw-semibold">
							{t('Auto Assignment')}
						</Label>
					</div>

				</div>
				<Button
					size="sm"
					color="primary"
					disabled={!enabled}
					onClick={() => {
						setEditRule(undefined);
						setWizOpen(true);
					}}
				>
					<i className="ri-add-line me-1" /> {t('Add Rule')}
				</Button>
			</div>

			{/* ─── Mode Toggle ─────────────────────────────────── */}
			{enabled && (
				<div>
					<div className="d-flex align-items-center gap-3">
						<Label className="fw-semibold mb-0">{t('Assignment Mode')}</Label>
						<div className="d-flex align-items-center border rounded-pill px-3 py-1 shadow-sm">
							<div
								className={`small px-2 fw-semibold ${mode === 'FIRST_MATCH' ? 'text-primary' : 'text-muted'}`}
								style={{ cursor: 'pointer' }}
								onClick={() => setMode('FIRST_MATCH')}
							>
								{t('First-Match')}
							</div>
							<div className="form-check form-switch mx-2">
								<input
									type="checkbox"
									className="form-check-input"
									id="modeSwitch"
									checked={mode === 'AGGREGATE'}
									onChange={() => setMode(mode === 'FIRST_MATCH' ? 'AGGREGATE' : 'FIRST_MATCH')}
								/>
							</div>
							<div
								className={`small px-2 fw-semibold ${mode === 'AGGREGATE' ? 'text-primary' : 'text-muted'}`}
								style={{ cursor: 'pointer' }}
								onClick={() => setMode('AGGREGATE')}
							>
								{t('Aggregate')}
							</div>
						</div>
					</div>

					<div className="form-text text-muted mt-1 mb-3">
						{mode === 'FIRST_MATCH'
							? t('Apply the first matching rule only. Order matters.')
							: t('Apply all matching rules and combine assignments.')}
					</div>
				</div>
			)}



			{/* ─── Rules List ──────────────────────────────────── */}
			{enabled && (
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="rules">
						{(provided: any) => (
							<div ref={provided.innerRef} {...provided.droppableProps} className="vstack gap-3">
								{rules.map((rule, index) => (
									<Draggable key={rule.id} draggableId={rule.id} index={index}>
										{(prov: any, snapshot: any) => (
											<div
												ref={prov.innerRef}
												{...prov.draggableProps}
												style={{
													// background: snapshot.isDragging ? '#f8f9fa' : '#fff',
													border: '1px solid #dee2e6',
													borderRadius: '0.75rem',
													...prov.draggableProps.style
												}}
											>
												<div
													className="d-flex justify-content-between align-items-center px-3 py-3"
													onClick={() => toggle(rule.id)}
												>
													<div className="d-flex align-items-center flex-grow-1">
														<span
															{...prov.dragHandleProps}
															style={{ cursor: 'grab', marginRight: 12 }}
															onClick={(e) => e.stopPropagation()}
														>
															<i className="ri-menu-line fs-5 text-muted"></i>
														</span>

														<div>
															<h6 className="mb-0 fw-bold">{index + 1}. {rule.name}</h6>
															<small className="text-muted">
																{STRATEGIES.find(s => s.value === rule.strategy)?.label}
															</small>
														</div>
													</div>

													<div className="d-flex align-items-center gap-2">
														<Button
															size="sm"
															color="info"
															onClick={(e) => {
																e.stopPropagation();
																setEditRule(rule);
																setWizOpen(true);
															}}
														>
															<i className="ri-pencil-line me-1" />{t('Edit')}
														</Button>
														<Button
															size="sm"
															color="danger"
															onClick={(e) => {
																e.stopPropagation();
																setRules((prev) =>
																	prev.filter((x) => x.id !== rule.id).map((x, idx) => ({ ...x, priority: idx + 1 }))
																);
															}}
														>
															<i className="ri-delete-bin-line me-1" />{t('Delete')}
														</Button>
														<Button
															size="sm"
															color="light"
															onClick={(e) => {
																e.stopPropagation();
																toggle(rule.id);
															}}
														>
															<i className={`ri-arrow-${openIds.includes(rule.id) ? 'up' : 'down'}-s-line`} />
														</Button>
													</div>
												</div>

												{/* ── Expanded Rule Content ── */}
												<Collapse isOpen={openIds.includes(rule.id)}>
													<div className="px-4 py-3 border-top rounded-bottom">
														{/* Filters */}
														<div className="mb-4 d-flex align-items-start">
															<div className="me-3">
																<div className="bg-warning-subtle text-warning-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
																	<i className="ri-filter-line fs-5" />
																</div>
															</div>
															<div className="flex-grow-1">
																<div className="d-flex justify-content-between align-items-center">
																	<small className="text-muted fw-semibold">{t('Filters')}</small>
																	<Badge color={rule.filters?.length ? 'info' : 'secondary'} pill>
																		{rule.filters?.length || 0}
																	</Badge>
																</div>
																{rule.filters?.length === 0 ? (
																	<small className="text-danger d-block mt-1">
																		<i className="ri-error-warning-line me-1" />
																		{t('No filters defined')}
																	</small>
																) : (
																	rule.filters.map((group, gIndex) => (
																		<div key={gIndex} className="border-start ps-2 mt-2">
																			<div className="fw-medium small text-muted mb-1">
																				{t('Group')} {gIndex + 1}
																			</div>
																			{group.filters.map((f: any, fIdx: number) => (
																				<div key={fIdx} className="text-muted small d-flex align-items-start mb-1">
																					<i className="ri-subtract-line text-muted me-1 pt-1" />
																					<div>
																						<span className="fw-semibold">{f.keyLabel}</span> {f.conditionLabel}{' '}
																						{f.valuesLabel?.map((v: any, i: number) => (
																							<span key={i}>
																								<Badge color="light" className="text-body">{v.label}</Badge>
																							</span>
																						))}
																						{f.value && f.highValue && (
																							<Badge color="light" className="text-body">{f.value} → {f.highValue}</Badge>
																						)}
																					</div>
																				</div>
																			))}
																		</div>
																	))
																)}
															</div>
														</div>

														{/* Assignments */}
														<div className="mb-2 d-flex align-items-start">
															<div className="me-3">
																<div className="bg-success-subtle text-success-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
																	<i className="ri-user-settings-line fs-5" />
																</div>
															</div>
															<div className="flex-grow-1">
																<div className="d-flex justify-content-between align-items-center">
																	<small className="text-muted fw-semibold">{t('Assignments')}</small>
																	<Badge
																		color={
																			Object.values(rule.assignments).some(a => a.values.length)
																				? 'success'
																				: 'secondary'
																		}
																		pill
																	>
																		{
																			Object.values(rule.assignments).reduce(
																				(acc, a) => acc + (a.values?.length || 0),
																				0
																			)
																		}
																	</Badge>
																</div>

																{Object.entries(rule.assignments)
																	.filter(([_, a]) => a.values.length > 0)
																	.map(([key, assignment]) => {
																		const fieldLabel = assignmentFields.find(f => f.key === key)?.label || key;
																		const modeLabel = {
																			users: t("By User"),
																			roles: t("By Role"),
																			teams: t("By Team")
																		}[assignment.type];

																		return (
																			<div key={key} className="mt-2">
																				<div className="small text-muted mb-1 fw-medium">
																					{fieldLabel} <span className="text-secondary">({modeLabel})</span>
																				</div>
																				<div className="d-flex flex-wrap gap-1">
																					{assignment.values.map((v: any, i: number) => (
																						<Badge color="light" className="text-body"> {v.label} </Badge>
																						// <span key={i} className="badge border border-light text-body">{v.label}</span>
																					))}
																				</div>
																			</div>
																		);
																	})}

																{Object.values(rule.assignments).every(a => !a.values.length) && (
																	<small className="text-danger d-block mt-1">
																		<i className="ri-error-warning-line me-1" />
																		{t('No assignments defined')}
																	</small>
																)}
															</div>
														</div>
													</div>
												</Collapse>
											</div>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			)}

			<AutoAssignRuleWizard
				isOpen={wizOpen}
				initialRule={editRule}
				assignmentFields={assignmentFields}
				metaDataFields={metaDataFields}
				onSave={(rule) => {
					setRules((prev) => {
						const exists = prev.some((x) => x.id === rule.id);
						const updated = exists ? prev.map((x) => (x.id === rule.id ? rule : x)) : [...prev, rule];
						return updated.map((x, i) => ({ ...x, priority: i + 1 }));
					});
					setWizOpen(false);
				}}
				onCancel={() => setWizOpen(false)}
			/>
		</div>
	);
};

export default withTranslation()(AutoAssignRules);
